import QRCode from "qrcode";
import { useEffect, useMemo, useRef, useState } from "react";
import { Action } from "../components/Actions";
import { EditorialShell } from "../components/EditorialShell";
import { albumByName } from "../data/albums";
import { displayDimension, toDisplayCopy } from "../logic/dimensions";
import { createShareText, invokeShareLink, getRuntimeShareUrl } from "../share";
import { INTERNAL_DIMENSIONS, type InternalDimension, type OfficialResult } from "../types";

type ExportStatus = "idle" | "rendering" | "saved" | "shared" | "copied" | "preview" | "error";

const CARD_WIDTH = 1080;
const CARD_HEIGHT = 1350;
const RADAR_CENTER = 100;
const RADAR_RADIUS = 64;

function statusMessage(status: ExportStatus) {
  if (status === "rendering") return "正在生成卡片…";
  if (status === "saved") return "PNG 已保存。";
  if (status === "shared") return "已打开系统分享。";
  if (status === "copied") return "已复制";
  if (status === "preview") return "长按图片保存";
  if (status === "error") return "这次没有生成成功，请再试一次。";
  return "";
}

function radarPoint(index: number, radius: number) {
  const angle = -Math.PI / 2 + (Math.PI * 2 * index) / INTERNAL_DIMENSIONS.length;
  return [RADAR_CENTER + Math.cos(angle) * radius, RADAR_CENTER + Math.sin(angle) * radius] as const;
}

function radarPolygon(radius: number) {
  return INTERNAL_DIMENSIONS.map((_, index) => radarPoint(index, radius).join(",")).join(" ");
}

function ShareRadar({ values }: { values: Record<InternalDimension, number> }) {
  const valuePoints = INTERNAL_DIMENSIONS.map((dimension, index) => radarPoint(index, RADAR_RADIUS * Math.max(0, Math.min(1, values[dimension] / 100))).join(",")).join(" ");
  return (
    <svg className="share-card__radar" viewBox="0 0 200 200" role="img" aria-label="六维人格简图">
      {[1, 0.66, 0.33].map((scale) => <polygon key={scale} points={radarPolygon(RADAR_RADIUS * scale)} className="share-card__radar-grid" />)}
      {INTERNAL_DIMENSIONS.map((_, index) => {
        const [x, y] = radarPoint(index, RADAR_RADIUS);
        return <line key={index} x1={RADAR_CENTER} y1={RADAR_CENTER} x2={x} y2={y} className="share-card__radar-axis" />;
      })}
      <polygon points={valuePoints} className="share-card__radar-value" />
      <circle cx={RADAR_CENTER} cy={RADAR_CENTER} r="12" className="share-card__radar-center" />
      <text x={RADAR_CENTER} y={RADAR_CENTER} textAnchor="middle" dominantBaseline="middle" className="share-card__radar-center-label">WMLS</text>
      {INTERNAL_DIMENSIONS.map((dimension, index) => {
        const [x, y] = radarPoint(index, RADAR_RADIUS + 24);
        return (
          <text key={dimension} x={x} y={y} textAnchor="middle" dominantBaseline="middle" className="share-card__radar-label">
            <tspan x={x} dy="-0.32em">{displayDimension(dimension)}</tspan>
            <tspan x={x} dy="1.25em" className="share-card__radar-score">{Math.round(values[dimension])}</tspan>
          </text>
        );
      })}
    </svg>
  );
}

function setCanvasFont(context: CanvasRenderingContext2D, size: number, family: "display" | "body" | "mono" | "wordmark", weight = 400) {
  const families = {
    display: '"Noto Serif SC", "Songti SC", serif',
    body: '"Noto Sans SC", "Microsoft YaHei", sans-serif',
    mono: '"IBM Plex Mono", Consolas, monospace',
    wordmark: '"Bodoni Moda", "Times New Roman", serif',
  };
  context.font = `${weight} ${size}px ${families[family]}`;
}

function wrapCanvasText(context: CanvasRenderingContext2D, text: string, maxWidth: number) {
  const lines: string[] = [];
  let line = "";
  for (const character of text) {
    const next = line + character;
    if (line && context.measureText(next).width > maxWidth) {
      lines.push(line);
      line = character;
    } else line = next;
  }
  if (line) lines.push(line);
  return lines;
}

function fitCanvasFont(context: CanvasRenderingContext2D, text: string, maxWidth: number, start: number, minimum: number) {
  let size = start;
  while (size > minimum) {
    setCanvasFont(context, size, "display", 500);
    if (context.measureText(text).width <= maxWidth) return size;
    size -= 2;
  }
  return minimum;
}

function drawCanvasRadar(context: CanvasRenderingContext2D, values: Record<InternalDimension, number>, centerX: number, centerY: number, radius: number) {
  const point = (index: number, value: number) => {
    const angle = -Math.PI / 2 + (Math.PI * 2 * index) / INTERNAL_DIMENSIONS.length;
    return [centerX + Math.cos(angle) * radius * value, centerY + Math.sin(angle) * radius * value] as const;
  };
  const drawPolygon = (scale: number) => {
    context.beginPath();
    INTERNAL_DIMENSIONS.forEach((_, index) => {
      const [x, y] = point(index, scale);
      if (index === 0) context.moveTo(x, y); else context.lineTo(x, y);
    });
    context.closePath();
  };
  context.strokeStyle = "rgba(111,137,166,.3)";
  context.lineWidth = 1.5;
  [1, 0.66, 0.33].forEach((scale) => { drawPolygon(scale); context.stroke(); });
  INTERNAL_DIMENSIONS.forEach((_, index) => {
    const [x, y] = point(index, 1);
    context.beginPath();
    context.moveTo(centerX, centerY);
    context.lineTo(x, y);
    context.stroke();
  });
  drawPolygon(1);
  context.beginPath();
  INTERNAL_DIMENSIONS.forEach((dimension, index) => {
    const [x, y] = point(index, Math.max(0, Math.min(1, values[dimension] / 100)));
    if (index === 0) context.moveTo(x, y); else context.lineTo(x, y);
  });
  context.closePath();
  context.fillStyle = "rgba(111,137,166,.2)";
  context.fill();
  context.strokeStyle = "#6F89A6";
  context.lineWidth = 2;
  context.stroke();
  context.fillStyle = "#FBFAF7";
  context.strokeStyle = "rgba(111,137,166,.55)";
  context.lineWidth = 1;
  context.beginPath();
  context.arc(centerX, centerY, 28, 0, Math.PI * 2);
  context.fill();
  context.stroke();
  setCanvasFont(context, 13, "mono", 400);
  context.fillStyle = "#57738F";
  context.textAlign = "center";
  context.textBaseline = "middle";
  context.fillText("WMLS", centerX, centerY + 1);
  INTERNAL_DIMENSIONS.forEach((dimension, index) => {
    const angle = -Math.PI / 2 + (Math.PI * 2 * index) / INTERNAL_DIMENSIONS.length;
    const x = centerX + Math.cos(angle) * (radius + 58);
    const y = centerY + Math.sin(angle) * (radius + 48);
    setCanvasFont(context, 20, "body", 400);
    context.fillStyle = "#3F4A53";
    context.fillText(displayDimension(dimension), x, y - 10);
    setCanvasFont(context, 15, "mono", 400);
    context.fillStyle = "#626C71";
    context.fillText(String(Math.round(values[dimension])), x, y + 16);
  });
}

function canvasToBlob(canvas: HTMLCanvasElement) {
  return new Promise<Blob>((resolve, reject) => canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error("PNG encoding failed")), "image/png"));
}

async function decodeImage(image: HTMLImageElement) {
  if (typeof image.decode === "function") {
    try {
      await image.decode();
      return;
    } catch (error) {
      if (image.complete && image.naturalWidth > 0) return;
      throw error;
    }
  }
  if (image.complete && image.naturalWidth > 0) return;
  await new Promise<void>((resolve, reject) => {
    image.addEventListener("load", () => resolve(), { once: true });
    image.addEventListener("error", () => reject(new Error(`Image failed to load: ${image.alt}`)), { once: true });
  });
}

async function copyToClipboard(text: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.append(textarea);
  textarea.select();
  const copied = document.execCommand("copy");
  textarea.remove();
  if (!copied) throw new Error("Clipboard unavailable");
}

export function OfficialResultCardScreen({ result, onBack, onCreateMatch }: { result: OfficialResult; onBack(): void; onCreateMatch?: () => void }) {
  const cardRef = useRef<HTMLElement>(null);
  const copiedTimerRef = useRef<number | null>(null);
  const [status, setStatus] = useState<ExportStatus>("idle");
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const [canShareFile, setCanShareFile] = useState(false);
  const [canShareLink, setCanShareLink] = useState(false);
  const shareText = useMemo(() => createShareText({ title: result.persona.title, judgement: result.persona.judgement }), [result.persona]);

  useEffect(() => {
    let active = true;
    const searchParams = new URLSearchParams(window.location.search);
    const forceShareFallback = import.meta.env.DEV && searchParams.has("share-fallback");
    const forceLinkFallback = import.meta.env.DEV && searchParams.has("share-link-fallback");
    QRCode.toDataURL(getRuntimeShareUrl(), {
      width: 320,
      margin: 2,
      errorCorrectionLevel: "M",
      color: { dark: "#1C2732", light: "#FFFFFF" },
    }).then((value) => { if (active) setQrDataUrl(value); }).catch(() => { if (active) setStatus("error"); });
    const probe = new File([new Uint8Array([0])], "wmlsti-result.png", { type: "image/png" });
    setCanShareFile(!forceShareFallback && typeof navigator.share === "function" && typeof navigator.canShare === "function" && navigator.canShare({ files: [probe] }));
    setCanShareLink(!forceLinkFallback && typeof navigator.share === "function");
    return () => {
      active = false;
      if (copiedTimerRef.current !== null) window.clearTimeout(copiedTimerRef.current);
    };
  }, []);

  async function renderCard() {
    if (!cardRef.current || !qrDataUrl) throw new Error("Result card assets are not ready");
    await document.fonts.ready;
    const covers = Array.from(cardRef.current.querySelectorAll<HTMLImageElement>(".share-card__songs img"));
    const qrImage = cardRef.current.querySelector<HTMLImageElement>(".share-card__qr img");
    if (!qrImage || covers.length !== result.expandedSongs.length) throw new Error("Result card images are not ready");
    await Promise.all([qrImage, ...covers].map(decodeImage));
    if ([qrImage, ...covers].some((image) => image.naturalWidth === 0)) throw new Error("Result card images are not ready");
    const canvas = document.createElement("canvas");
    canvas.width = CARD_WIDTH;
    canvas.height = CARD_HEIGHT;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Canvas unavailable");
    context.fillStyle = "#F7F6F2";
    context.fillRect(0, 0, CARD_WIDTH, CARD_HEIGHT);
    context.strokeStyle = "rgba(111,137,166,.11)";
    context.lineWidth = 2;
    [120, 170, 220, 270].forEach((radius) => {
      context.beginPath();
      context.arc(1040, 40, radius, 0, Math.PI * 2);
      context.stroke();
    });
    setCanvasFont(context, 50, "wordmark", 500);
    context.fillStyle = "#1C2732";
    context.textAlign = "left";
    context.textBaseline = "alphabetic";
    context.fillText("WMLSTI", 72, 104);
    setCanvasFont(context, 19, "display", 400);
    context.fillStyle = "#626C71";
    context.fillText("五月天人生观测试", 74, 140);

    const title = toDisplayCopy(result.persona.title);
    const titleSize = fitCanvasFont(context, title, 936, 86, 58);
    setCanvasFont(context, titleSize, "display", 500);
    context.fillStyle = "#6F89A6";
    context.fillText(title, 72, 276);
    setCanvasFont(context, 31, "display", 400);
    context.fillStyle = "#1C2732";
    wrapCanvasText(context, toDisplayCopy(result.persona.judgement), 860).slice(0, 2).forEach((line, index) => context.fillText(line, 74, 338 + index * 48));

    context.strokeStyle = "rgba(28,39,50,.12)";
    context.lineWidth = 2;
    context.beginPath();
    context.moveTo(72, 414);
    context.lineTo(1008, 414);
    context.stroke();

    drawCanvasRadar(context, result.classification.percentiles, 300, 705, 140);
    context.textAlign = "left";
    context.textBaseline = "alphabetic";

    setCanvasFont(context, 20, "body", 400);
    context.fillStyle = "#626C71";
    context.fillText("我的代表歌曲", 580, 500);
    result.expandedSongs.forEach((item, index) => {
      const top = 540 + index * 142;
      context.drawImage(covers[index], 580, top, 96, 96);
      setCanvasFont(context, 17, "mono", 400);
      context.fillStyle = "#57738F";
      context.fillText(String(index + 1).padStart(2, "0"), 706, top + 28);
      setCanvasFont(context, 29, "display", 500);
      context.fillStyle = "#1C2732";
      const titleLines = wrapCanvasText(context, item.song.song, 262).slice(0, 2);
      titleLines.forEach((line, lineIndex) => context.fillText(line, 748, top + 36 + lineIndex * 36));
      setCanvasFont(context, 17, "body", 400);
      context.fillStyle = "#626C71";
      const albumY = titleLines.length > 1 ? top + 96 : top + 78;
      context.fillText(item.song.album, 748, albumY);
    });

    context.strokeStyle = "rgba(28,39,50,.12)";
    context.beginPath();
    context.moveTo(72, 1050);
    context.lineTo(1008, 1050);
    context.stroke();
    context.drawImage(qrImage, 72, 1110, 168, 168);
    setCanvasFont(context, 29, "display", 500);
    context.fillStyle = "#3F4A53";
    context.fillText("扫码测测你的 WMLS 人格", 286, 1190);
    setCanvasFont(context, 19, "body", 400);
    context.fillStyle = "#626C71";
    context.fillText("九张专辑，各选一首。", 286, 1230);
    return canvas;
  }

  async function makeArtifact() {
    const canvas = await renderCard();
    const dataUrl = canvas.toDataURL("image/png");
    const blob = await canvasToBlob(canvas);
    const file = new File([blob], `WMLSTI-${result.persona.title}.png`, { type: "image/png" });
    return { dataUrl, blob, file };
  }

  function openPreview(dataUrl: string) {
    setPreviewUrl(dataUrl);
    setStatus("preview");
  }

  function reportExportError(error: unknown) {
    if (import.meta.env.DEV) document.documentElement.dataset.shareCardError = error instanceof Error ? `${error.name}: ${error.message}` : String(error);
    setStatus("error");
  }

  async function saveCard() {
    try {
      setStatus("rendering");
      const artifact = await makeArtifact();
      const coarsePointer = window.matchMedia("(pointer: coarse)").matches;
      if (coarsePointer && canShareFile) {
        await navigator.share({ files: [artifact.file], title: "我的 WMLS 人格", text: result.persona.judgement });
        setStatus("shared");
        return;
      }
      if (coarsePointer) {
        openPreview(artifact.dataUrl);
        return;
      }
      const objectUrl = URL.createObjectURL(artifact.blob);
      const link = document.createElement("a");
      link.download = artifact.file.name;
      link.href = objectUrl;
      link.click();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      setStatus("saved");
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") setStatus("idle");
      else reportExportError(error);
    }
  }

  async function shareImage() {
    try {
      setStatus("rendering");
      const artifact = await makeArtifact();
      if (canShareFile) {
        await navigator.share({ files: [artifact.file], title: "我的 WMLS 人格", text: result.persona.judgement });
        setStatus("shared");
      } else openPreview(artifact.dataUrl);
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") setStatus("idle");
      else reportExportError(error);
    }
  }

  async function copyShareCopy() {
    try {
      await copyToClipboard(shareText);
      setStatus("copied");
      if (copiedTimerRef.current !== null) window.clearTimeout(copiedTimerRef.current);
      copiedTimerRef.current = window.setTimeout(() => setStatus("idle"), 1500);
    } catch {
      setStatus("error");
    }
  }

  async function shareLink() {
    if (!canShareLink) {
      await copyShareCopy();
      return;
    }
    try {
      await invokeShareLink({ title: result.persona.title, judgement: result.persona.judgement });
      setStatus("shared");
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      setStatus("error");
    }
  }

  return (
    <EditorialShell className="share-card-page">
      <section className="share-card-layout result-page-enter" aria-labelledby="share-card-title">
        <header className="share-card-heading">
          <div>
            <h1 id="share-card-title">你的结果卡片</h1>
            <p>保存图片，或把结果分享给朋友。</p>
          </div>
          <span>4 : 5</span>
        </header>

        <article ref={cardRef} className="share-card" aria-label={`${result.persona.title}结果卡片预览`}>
          <header className="share-card__brand">
            <div><strong>WMLSTI</strong><span>五月天人生观测试</span></div>
          </header>

          <div className="share-card__persona">
            <h2>{toDisplayCopy(result.persona.title)}</h2>
            <blockquote>{toDisplayCopy(result.persona.judgement)}</blockquote>
          </div>

          <div className="share-card__core">
            <ShareRadar values={result.classification.percentiles} />
            <section className="share-card__songs" aria-label="我的代表歌曲">
              <p className="share-card__songs-label">我的代表歌曲</p>
              {result.expandedSongs.map((item, index) => {
                const album = albumByName.get(item.song.album);
                return (
                  <div key={`${item.song.album}-${item.song.song}`}>
                    <img src={album?.cover} alt={`${item.song.album}专辑封面`} />
                    <span>{String(index + 1).padStart(2, "0")}</span>
                    <p className="share-card__song-copy"><strong>{item.song.song}</strong><small>{item.song.album}</small></p>
                  </div>
                );
              })}
            </section>
          </div>

          <footer className="share-card__qr">
            {qrDataUrl ? <img src={qrDataUrl} alt="WMLSTI 正式测试二维码" /> : <span className="share-card__qr-loading" aria-hidden="true" />}
            <div><strong>扫码测测你的 WMLS 人格</strong><span>九张专辑，各选一首。</span></div>
          </footer>
        </article>

        <div className="share-card-actions">
          <Action onClick={saveCard} disabled={status === "rendering" || !qrDataUrl}>{status === "rendering" ? "正在生成" : "保存结果卡片"}</Action>
          <Action tone="secondary" onClick={shareImage} disabled={status === "rendering" || !qrDataUrl}>分享图片</Action>
          <Action tone="secondary" onClick={shareLink}>分享链接</Action>
          {onCreateMatch ? <Action tone="secondary" onClick={onCreateMatch}>邀请好友测默契</Action> : null}
          <Action tone="secondary" onClick={copyShareCopy}>复制分享文案</Action>
          <Action tone="text" onClick={onBack}>← 返回结果</Action>
        </div>
        <p className="share-card-status" aria-live="polite">{statusMessage(status)}</p>
      </section>

      {previewUrl ? (
        <div className="share-card-preview" role="dialog" aria-modal="true" aria-labelledby="share-card-preview-title">
          <div className="share-card-preview__toolbar">
            <div><strong id="share-card-preview-title">长按图片保存</strong><span>图片已经生成完成</span></div>
            <button type="button" onClick={() => { setPreviewUrl(""); setStatus("idle"); }} aria-label="关闭图片预览">关闭</button>
          </div>
          <img src={previewUrl} alt={`${result.persona.title}高清结果卡片`} />
        </div>
      ) : null}
    </EditorialShell>
  );
}
